import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, X, Upload } from "lucide-react";
import ImageUploader from "../components/ImageUploader";
import PdfUploader from "../components/PdfUploader";

const STATUSES = [
  { value: "upcoming",       label: "Upcoming" },
  { value: "under_planning", label: "Under Planning" },
  { value: "approved",       label: "Approved" },
  { value: "in_development", label: "In Development" },
];

const EMPTY = {
  id: null,
  title: "",
  description: "",
  image_url: "",
  pdfs: [],
  location: "",
  status: "upcoming",
  timeline: "",
  developer_name: "",
  highlights: [],
  is_active: true,
};

export default function AdminMasterPlans() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form state or null
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/master-plans");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load master plans");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEditing({ ...EMPTY }); }
  function openEdit(mp) { setEditing({ ...EMPTY, ...mp, highlights: mp.highlights || [] }); }
  function close() { setEditing(null); }

  async function save() {
    if (!editing) return;
    const wordCount = (editing.description || "").trim().split(/\s+/).filter(Boolean).length;
    if (!editing.title.trim() || editing.title.trim().length < 3) return toast.error("Title must be at least 3 characters.");
    if (wordCount < 20) return toast.error(`Description must be at least 20 words (currently ${wordCount}).`);
    if (!editing.image_url) return toast.error("Please upload an image.");
    if (!editing.location.trim()) return toast.error("Location is required.");

    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        description: editing.description.trim(),
        image_url: editing.image_url,
        pdfs: (editing.pdfs || []).filter((p) => (p || "").trim()),
        location: editing.location.trim(),
        status: editing.status,
        timeline: editing.timeline.trim(),
        developer_name: editing.developer_name.trim(),
        highlights: (editing.highlights || []).map((h) => (h || "").trim()).filter(Boolean),
        is_active: !!editing.is_active,
      };
      if (editing.id) {
        await api.put(`/admin/master-plans/${editing.id}`, payload);
        toast.success("Master plan updated.");
      } else {
        await api.post(`/admin/master-plans`, payload);
        toast.success("Master plan created.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function toggle(id) {
    try { await api.post(`/admin/master-plans/${id}/toggle`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this master plan? This cannot be undone.")) return;
    try { await api.delete(`/admin/master-plans/${id}`); toast.success("Deleted."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  function setF(k, v) { setEditing((p) => ({ ...p, [k]: v })); }
  function setHighlight(i, v) { setEditing((p) => ({ ...p, highlights: p.highlights.map((h, idx) => idx === i ? v : h) })); }
  function addHighlight() { setEditing((p) => ({ ...p, highlights: [...(p.highlights || []), ""] })); }
  function removeHighlight(i) { setEditing((p) => ({ ...p, highlights: p.highlights.filter((_, idx) => idx !== i) })); }

  const wordCount = (editing?.description || "").trim().split(/\s+/).filter(Boolean).length;

  return (
    <div data-testid="admin-master-plans">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-xl font-bold text-ink">Master Plans</h2>
          <p className="text-sm text-muted-foreground">Add, edit and publish city / area master plans.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary text-white font-semibold text-sm hover:bg-primary/90" data-testid="mp-new-btn">
          <Plus className="h-4 w-4" /> Add New Plan
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-border rounded-xl">
          <p className="font-display text-lg font-semibold">No master plans yet</p>
          <p className="text-sm text-muted-foreground">Click <em>Add New Plan</em> to publish your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((mp) => (
            <div key={mp.id} className="bg-white border border-border rounded-xl overflow-hidden flex" data-testid={`mp-row-${mp.id}`}>
              <img src={mp.image_url} alt={mp.title} className="w-32 h-32 object-cover shrink-0" />
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-sm line-clamp-2">{mp.title}</h3>
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border shrink-0 ${mp.is_active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{mp.is_active ? "Live" : "Hidden"}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{mp.location} · {STATUSES.find(s => s.value === mp.status)?.label}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{mp.description}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button onClick={() => openEdit(mp)} className="inline-flex items-center gap-1 h-8 px-2.5 rounded border border-border text-xs hover:bg-slate-50"><Pencil className="h-3 w-3" /> Edit</button>
                  <button onClick={() => toggle(mp.id)} className="inline-flex items-center gap-1 h-8 px-2.5 rounded border border-border text-xs hover:bg-slate-50">
                    {mp.is_active ? <><EyeOff className="h-3 w-3" /> Unpublish</> : <><Eye className="h-3 w-3" /> Publish</>}
                  </button>
                  <button onClick={() => remove(mp.id)} className="inline-flex items-center gap-1 h-8 px-2.5 rounded border border-rose-200 text-rose-700 bg-rose-50/50 text-xs hover:bg-rose-100"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mp-modal">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl font-bold">{editing.id ? "Edit" : "New"} Master Plan</h3>
                <button onClick={close} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-ink block mb-1">Title *</label>
                  <input value={editing.title} onChange={(e) => setF("title", e.target.value)} className="w-full h-10 px-3 border border-border rounded-md text-sm" data-testid="mp-title" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-ink block mb-1">Description * <span className="font-normal text-muted-foreground">(min 20 words — {wordCount} words)</span></label>
                  <textarea value={editing.description} onChange={(e) => setF("description", e.target.value)} rows={5} className="w-full px-3 py-2 border border-border rounded-md text-sm" data-testid="mp-description" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-ink block mb-1">Image *</label>
                  <ImageUploader images={editing.image_url ? [editing.image_url] : []} onChange={(imgs) => setF("image_url", imgs[0] || "")} max={1} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-ink block mb-1">PDFs <span className="text-muted-foreground font-normal">(Optional — brochure, layout, official plan)</span></label>
                  <PdfUploader value={editing.pdfs || []} onChange={(pdfs) => setF("pdfs", pdfs)} multiple maxMb={50} />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-ink block mb-1">Location *</label>
                    <input value={editing.location} onChange={(e) => setF("location", e.target.value)} className="w-full h-10 px-3 border border-border rounded-md text-sm" placeholder="e.g. Sector 66, Gurgaon" data-testid="mp-location" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-ink block mb-1">Status</label>
                    <select value={editing.status} onChange={(e) => setF("status", e.target.value)} className="w-full h-10 px-3 border border-border rounded-md text-sm bg-white" data-testid="mp-status">
                      {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-ink block mb-1">Timeline</label>
                    <input value={editing.timeline} onChange={(e) => setF("timeline", e.target.value)} className="w-full h-10 px-3 border border-border rounded-md text-sm" placeholder="e.g. 2025–2030" data-testid="mp-timeline" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-ink block mb-1">Developer <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input value={editing.developer_name} onChange={(e) => setF("developer_name", e.target.value)} className="w-full h-10 px-3 border border-border rounded-md text-sm" data-testid="mp-developer" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-ink">Highlights (roads, zones, infrastructure)</label>
                    <button type="button" onClick={addHighlight} className="text-xs font-semibold text-primary hover:underline">+ Add</button>
                  </div>
                  <div className="space-y-2 mt-2">
                    {(editing.highlights || []).map((h, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input value={h} onChange={(e) => setHighlight(i, e.target.value)} className="flex-1 h-9 px-3 border border-border rounded-md text-sm" placeholder="e.g. New 6-lane road connecting to NH-48" data-testid={`mp-highlight-${i}`} />
                        <button type="button" onClick={() => removeHighlight(i)} className="h-9 w-9 rounded-md border border-border text-rose-600 hover:bg-rose-50 flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setF("is_active", e.target.checked)} className="h-4 w-4" data-testid="mp-active" />
                  <span>Publish to public Master Plan page</span>
                </label>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button onClick={close} className="h-10 px-4 rounded-md border border-border text-sm">Cancel</button>
                <button onClick={save} disabled={saving} className="h-10 px-5 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2" data-testid="mp-save-btn">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing.id ? "Save changes" : "Create plan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
